import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { MessageSquare, ArrowRight, Loader2, Inbox } from 'lucide-react';

export default function MessagesPage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [data, setData] = useState({ total: 0, dossiers: [] });
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api().get('/simulations/chat/unread-summary');
      setData(res.data || { total: 0, dossiers: [] });
    } catch (e) { /* silencieux */ }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => {
    fetchSummary();
    const t = setInterval(fetchSummary, 20000);
    return () => clearInterval(t);
  }, [fetchSummary]);

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="messages-page">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 text-orange-600 text-xs font-semibold mb-2">
            <MessageSquare className="w-3.5 h-3.5" />
            Messagerie des dossiers
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 dark:text-white">
            Messages non lus
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {data.total > 0
              ? `Vous avez ${data.total} message(s) non lu(s) sur ${data.dossiers.length} dossier(s).`
              : 'Retrouvez ici tous les dossiers avec de nouveaux messages.'}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
          </div>
        ) : data.dossiers.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-12 text-center" data-testid="messages-empty">
            <Inbox className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Aucun message non lu. Vous êtes à jour ! 🎉</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="messages-tiles">
            {data.dossiers.map((d) => (
              <button
                key={d.simulation_id}
                type="button"
                onClick={() => navigate(`/simulation/${d.simulation_id}`)}
                className="group text-left rounded-2xl p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-orange-400 hover:shadow-lg transition-all duration-200 flex flex-col gap-3"
                data-testid={`message-tile-${d.simulation_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="shrink-0 w-9 h-9 rounded-xl grid place-items-center bg-orange-500/15 text-orange-500">
                      <MessageSquare className="w-4 h-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 dark:text-white text-sm truncate">{d.project_name}</p>
                      {d.retailer_name && (
                        <p className="text-xs text-slate-400 truncate">{d.retailer_name}</p>
                      )}
                    </div>
                  </div>
                  <span className="shrink-0 inline-flex items-center justify-center min-w-[1.4rem] h-6 px-2 rounded-full bg-orange-500 text-white text-xs font-bold" data-testid={`message-tile-count-${d.simulation_id}`}>
                    {d.unread_count}
                  </span>
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
                  <span className="font-medium text-slate-500">{d.last_author} :</span> {d.last_message}
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-slate-400">{fmt(d.last_message_at)}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 group-hover:translate-x-0.5 transition-transform">
                    Ouvrir <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
